import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  Autocomplete,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import colorsStore from 'store/colorsStore';
import { ICharacteristicValue, IColor } from 'types/types';
import api from 'api';

const ColorEdit: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [hexCode, setHexCode] = useState('#000000');
  const [hexCode2, setHexCode2] = useState('#000000');
  const [slug, setSlug] = useState<string | null>(null);
  const [customH1, setCustomH1] = useState<string | null>(null);
  const [customTitle, setCustomTitle] = useState<string | null>(null);
  const [customDescription, setCustomDescription] = useState<string | null>(
    null
  );
  const [textContent, setTextContent] = useState<string | null>(null);
  const [values, setValues] = useState<ICharacteristicValue[]>([]);
  const [options, setOptions] = useState<ICharacteristicValue[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const isCreate = id === 'create';

  // Загрузка данных при редактировании
  useEffect(() => {
    if (!isCreate) {
      const loadColor = async () => {
        setLoading(true);
        const res = await colorsStore.getColor(Number(id));
        if (!res.isOk) {
          setError(res.msg);
        }
        setLoading(false);
      };
      loadColor();
    }
  }, [id, isCreate]);

  // Инициализация полей из store
  useEffect(() => {
    if (!isCreate && colorsStore.color) {
      const { color } = colorsStore;
      setName(color.name);
      setHexCode(color.hex_code);
      setHexCode2(color.hex_code_2 ?? '#000000');
      setSlug(color.slug ?? null);
      setCustomH1(color.custom_h1 ?? null);
      setCustomTitle(color.custom_title ?? null);
      setCustomDescription(color.custom_description ?? null);
      setTextContent(color.text_content ?? null);
      setValues(color.values || []);
    }
  }, [colorsStore.color, id, isCreate]);

  // Загрузка опций для автокомплита
  useEffect(() => {
    let active = true;

    if (!inputValue) {
      setOptions([]);
      return;
    }

    (async () => {
      try {
        const response = await colorsStore.getColorValues(inputValue);
        if (active) {
          const filtered = response.data.filter(
            (v: ICharacteristicValue) => !values.some((val) => val.id === v.id)
          );
          setOptions(filtered);
        }
      } catch (err) {
        if (active) {
          setOptions([]);
        }
      }
    })();

    // eslint-disable-next-line consistent-return
    return () => {
      active = false;
    };
  }, [inputValue, values]);

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    const data: Partial<IColor> = {
      name,
      hex_code: hexCode,
      hex_code_2: hexCode2,
      slug,
      custom_h1: customH1,
      custom_title: customTitle,
      custom_description: customDescription,
      text_content: textContent,
      values: values.map((v) => ({
        id: v.id,
        value: v.value,
        characteristic_type_id: v.characteristic_type_id
      }))
    };

    const valueIds = values
      .map((v) => v.id)
      .filter((valueId): valueId is number => typeof valueId === 'number');

    let res;
    if (isCreate) {
      const addRes = await colorsStore.addColor({
        name,
        hex_code: hexCode,
        hex_code_2: hexCode2
      });
      if (addRes.isOk && colorsStore.color?.id) {
        const updateRes = await colorsStore.editColor(
          colorsStore.color.id,
          data
        );
        if (!updateRes.isOk) {
          setError(updateRes.msg);
          return;
        }

        await api.colors.setColorValues(colorsStore.color.id, valueIds);
        setSuccess('Цвет успешно добавлен');
      } else {
        setError(addRes.msg);
        return;
      }
    } else {
      res = await colorsStore.editColor(Number(id), data);
      if (res?.isOk) {
        await api.colors.setColorValues(Number(id), valueIds);
      }
    }

    if ((isCreate && !error) || (!isCreate && res?.isOk)) {
      setTimeout(() => navigate('/complex-attributes/colors'), 300);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {isCreate ? 'Создание цвета' : `Редактирование цвета #${id}`}
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && <CircularProgress size={24} />}

      <Stack spacing={3} sx={{ mt: 2, maxWidth: 600 }}>
        <TextField
          label="Название"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            label="HEX код"
            value={hexCode}
            onChange={(e) => setHexCode(e.target.value)}
            fullWidth
            inputProps={{ maxLength: 7 }}
            placeholder="#000000"
          />
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '8px',
              border: '1px solid #ccc',
              backgroundColor: hexCode,
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              cursor: 'pointer',
              flexShrink: 0
            }}
            onClick={() => document.getElementById('color-input')?.click()}
          />
          <input
            id="color-input"
            type="color"
            value={hexCode}
            onChange={(e) => setHexCode(e.target.value)}
            style={{ visibility: 'hidden', position: 'absolute' }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            label="HEX код 2"
            value={hexCode2}
            onChange={(e) => setHexCode2(e.target.value)}
            fullWidth
            inputProps={{ maxLength: 7 }}
            placeholder="#000000"
          />
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '8px',
              border: '1px solid #ccc',
              backgroundColor: hexCode2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              cursor: 'pointer',
              flexShrink: 0
            }}
            onClick={() => document.getElementById('color2-input')?.click()}
          />
          <input
            id="color2-input"
            type="color"
            value={hexCode2}
            onChange={(e) => setHexCode2(e.target.value)}
            style={{ visibility: 'hidden', position: 'absolute' }}
          />
        </Box>

        <Autocomplete
          multiple
          options={options}
          getOptionLabel={(option) => option.value}
          value={values}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          onChange={(event, newValue) => {
            setValues(newValue);
          }}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                label={option.value}
                {...getTagProps({ index })}
                key={option.id}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Значения характеристик (цвета)"
              placeholder="Начните вводить..."
            />
          )}
          noOptionsText="Нет совпадений"
          loadingText="Загрузка..."
          filterOptions={(x) => x}
        />

        <TextField
          label="Slug"
          value={slug ?? ''}
          onChange={(e) => setSlug(e.target.value || null)}
          fullWidth
        />
        <TextField
          label="Custom H1"
          value={customH1 ?? ''}
          onChange={(e) => setCustomH1(e.target.value || null)}
          fullWidth
        />
        <TextField
          label="Custom Title"
          value={customTitle ?? ''}
          onChange={(e) => setCustomTitle(e.target.value || null)}
          fullWidth
        />
        <TextField
          label="Custom Description"
          value={customDescription ?? ''}
          onChange={(e) => setCustomDescription(e.target.value || null)}
          fullWidth
        />
        <TextField
          label="Текстовое содержание"
          multiline
          rows={4}
          value={textContent ?? ''}
          onChange={(e) => setTextContent(e.target.value || null)}
          fullWidth
        />

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={loading}
          >
            Сохранить
          </Button>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Отмена
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default observer(ColorEdit);
